<template>
  <component :is="layoutComponent" v-bind="layoutProps">
    <div :class="containerClass">
      <div v-if="!widgetMode" class="border-b border-slate-200 px-4 py-3">
        <h3 class="text-lg font-bold text-slate-900">Premium Chat</h3>
        <p class="text-xs text-slate-500">Only active premium members can access chat.</p>
      </div>

      <div :class="chatGridClass">
        <aside class="border-r border-slate-200">
          <div :class="conversationListClass">
            <button
              v-for="item in conversations"
              :key="item.id"
              type="button"
              class="w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50"
              :class="activeConversationId === item.id ? 'bg-cyan-50' : ''"
              @click="openConversation(item.id)"
            >
              <div class="flex items-center justify-between gap-2">
                <p class="truncate text-sm font-semibold text-slate-900">{{ item.target_user?.name || 'Member' }}</p>
                <span
                  v-if="item.unread_count > 0"
                  class="rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-bold text-white"
                >
                  {{ item.unread_count }}
                </span>
              </div>
              <p class="mt-1 text-xs text-slate-500">{{ item.last_message?.body || 'Start conversation' }}</p>
              <div class="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
                <span
                  class="h-2 w-2 rounded-full"
                  :class="item.target_user?.is_online ? 'bg-emerald-500' : 'bg-slate-300'"
                ></span>
                <span>{{ item.target_user?.is_online ? 'Online' : 'Offline' }}</span>
              </div>
            </button>
            <div v-if="!conversations.length" class="p-4 text-xs text-slate-500">
              No conversations yet.
            </div>
          </div>
        </aside>

        <section :class="chatSectionClass">
          <div class="border-b border-slate-200 px-4 py-3" v-if="activeConversation">
            <p class="text-sm font-semibold text-slate-900">{{ activeConversation.target_user?.name || 'Member' }}</p>
            <p class="text-xs text-slate-500">
              {{ activeConversation.target_user?.is_online ? 'Online now' : 'Not online right now' }}
            </p>
          </div>

          <div ref="messageListEl" class="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4">
            <div v-if="activeConversationId && !messages.length" class="text-xs text-slate-500">
              No messages yet. Send the first message.
            </div>
            <div
              v-for="message in messages"
              :key="message.id"
              class="max-w-[78%] rounded-xl px-3 py-2 text-sm shadow-sm"
              :class="message.sender_user_id === authUserId ? 'ml-auto bg-cyan-600 text-white' : 'bg-white text-slate-800'"
            >
              <p class="whitespace-pre-wrap break-words">{{ message.body }}</p>
              <p
                class="mt-1 text-[10px]"
                :class="message.sender_user_id === authUserId ? 'text-cyan-100' : 'text-slate-400'"
              >
                {{ formatTime(message.sent_at) }}
              </p>
            </div>
            <div v-if="!activeConversationId" class="text-xs text-slate-500">
              Select a conversation to view messages.
            </div>
          </div>

          <form class="border-t border-slate-200 bg-white p-3" @submit.prevent="sendMessage">
            <div class="flex gap-2">
              <input
                v-model="draftMessage"
                type="text"
                class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                placeholder="Type a message"
                :disabled="!activeConversationId || sending"
                maxlength="2000"
              />
              <button
                type="submit"
                class="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!activeConversationId || !draftMessage.trim() || sending"
              >
                {{ sending ? 'Sending...' : 'Send' }}
              </button>
            </div>
            <p v-if="errorText" class="mt-2 text-xs text-rose-600">{{ errorText }}</p>
          </form>
        </section>
      </div>
    </div>
  </component>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import UserDashboardLayout from '@/Layouts/UserDashboardLayout.vue';

const props = defineProps({
  initialTargetUserId: {
    type: Number,
    default: null,
  },
});

const page = usePage();
const authUserId = computed(() => Number(page.props?.auth?.user?.id || 0));
const widgetMode = computed(() => {
  const [, queryString = ''] = String(page.url || '').split('?');
  const params = new URLSearchParams(queryString);
  return params.get('widget') === '1';
});
const layoutComponent = computed(() => (widgetMode.value ? 'div' : UserDashboardLayout));
const layoutProps = computed(() => {
  if (widgetMode.value) {
    return {};
  }

  return {
    activeModule: 'chat',
    showChatLauncher: false,
  };
});
const containerClass = computed(() => {
  if (widgetMode.value) {
    return 'h-full bg-white';
  }

  return 'rounded-2xl border border-slate-200 bg-white shadow-sm';
});
const chatGridClass = computed(() => {
  if (widgetMode.value) {
    return 'grid h-[calc(70vh-44px)] min-h-[420px] grid-cols-1 md:grid-cols-[220px_1fr]';
  }

  return 'grid min-h-[560px] grid-cols-1 md:grid-cols-[300px_1fr]';
});
const conversationListClass = computed(() => {
  if (widgetMode.value) {
    return 'max-h-[calc(70vh-44px)] min-h-[420px] overflow-y-auto';
  }

  return 'max-h-[560px] overflow-y-auto';
});
const chatSectionClass = computed(() => {
  if (widgetMode.value) {
    return 'flex min-h-[420px] flex-col';
  }

  return 'flex min-h-[560px] flex-col';
});

const conversations = ref([]);
const activeConversationId = ref(null);
const messages = ref([]);
const draftMessage = ref('');
const sending = ref(false);
const errorText = ref('');
const messageListEl = ref(null);

const activeConversation = computed(() => {
  return conversations.value.find((item) => item.id === activeConversationId.value) || null;
});

let refreshTimer = null;
let heartbeatTimer = null;
let userChannel = null;

const formatTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const scrollToBottom = async () => {
  await nextTick();
  if (messageListEl.value) {
    messageListEl.value.scrollTop = messageListEl.value.scrollHeight;
  }
};

const loadConversations = async () => {
  const response = await window.axios.get('/chat/conversations');
  conversations.value = Array.isArray(response.data?.conversations) ? response.data.conversations : [];

  if (!activeConversationId.value && conversations.value.length) {
    activeConversationId.value = conversations.value[0].id;
  }
};

const loadMessages = async () => {
  if (!activeConversationId.value) {
    messages.value = [];
    return;
  }

  const response = await window.axios.get(`/chat/conversations/${activeConversationId.value}/messages`);
  messages.value = Array.isArray(response.data?.messages) ? response.data.messages : [];
  await window.axios.post(`/chat/conversations/${activeConversationId.value}/read`);
  await scrollToBottom();
};

const openConversation = async (conversationId) => {
  activeConversationId.value = conversationId;
  await loadMessages();
  await loadConversations();

  conversations.value = conversations.value.map((item) => {
    if (Number(item.id) !== Number(conversationId)) {
      return item;
    }

    return {
      ...item,
      unread_count: 0,
    };
  });
};

const sendMessage = async () => {
  const body = draftMessage.value.trim();
  if (!activeConversationId.value || !body) {
    return;
  }

  sending.value = true;
  errorText.value = '';

  try {
    const response = await window.axios.post(`/chat/conversations/${activeConversationId.value}/messages`, { body });
    draftMessage.value = '';

    const newMessage = response?.data?.message;
    if (newMessage && Number(newMessage.id) > 0) {
      messages.value = [...messages.value, newMessage];
      await scrollToBottom();
    }

    await loadConversations();
  } catch (error) {
    errorText.value = error?.response?.data?.message || 'Unable to send message right now.';
  } finally {
    sending.value = false;
  }
};

const heartbeat = async () => {
  await window.axios.post('/chat/presence/heartbeat');
};

const startConversationIfNeeded = async () => {
  if (!props.initialTargetUserId) {
    return;
  }

  const response = await window.axios.post('/chat/conversations', {
    target_user_id: props.initialTargetUserId,
  });

  if (response.data?.conversation_id) {
    activeConversationId.value = Number(response.data.conversation_id);
  }
};

const applyPresenceUpdate = (event) => {
  const targetUserId = Number(event?.user_id || 0);
  if (!targetUserId) {
    return;
  }

  conversations.value = conversations.value.map((item) => {
    if (Number(item?.target_user?.id || 0) !== targetUserId) {
      return item;
    }

    return {
      ...item,
      target_user: {
        ...(item.target_user || {}),
        is_online: Boolean(event?.is_online),
        last_seen_at: event?.last_seen_at || null,
      },
    };
  });
};

const applyIncomingMessage = async (event) => {
  const conversationId = Number(event?.conversation_id || 0);
  const incomingMessage = event?.message;

  if (!conversationId || !incomingMessage) {
    return;
  }

  const incomingSenderId = Number(incomingMessage?.sender_user_id || 0);
  const isActiveConversation = Number(activeConversationId.value) === conversationId;

  conversations.value = conversations.value.map((item) => {
    if (Number(item.id) !== conversationId) {
      return item;
    }

    const existingUnread = Number(item.unread_count || 0);
    const nextUnread = isActiveConversation || incomingSenderId === Number(authUserId.value)
      ? 0
      : existingUnread + 1;

    return {
      ...item,
      last_message: {
        id: Number(incomingMessage.id || 0),
        body: String(incomingMessage.body || ''),
        sent_at: incomingMessage.sent_at || null,
        sender_user_id: incomingSenderId,
      },
      unread_count: nextUnread,
    };
  });

  const currentItem = conversations.value.find((item) => Number(item.id) === conversationId);
  if (currentItem) {
    conversations.value = [
      currentItem,
      ...conversations.value.filter((item) => Number(item.id) !== conversationId),
    ];
  }

  if (isActiveConversation) {
    const alreadyPresent = messages.value.some((item) => Number(item.id) === Number(incomingMessage.id));
    if (!alreadyPresent) {
      messages.value = [...messages.value, incomingMessage];
      await scrollToBottom();
    }

    await window.axios.post(`/chat/conversations/${conversationId}/read`);
  }
};

const setupRealtime = () => {
  if (!window.Echo || Number(authUserId.value) <= 0) {
    return;
  }

  userChannel = window.Echo.private(`user.${authUserId.value}`);
  userChannel.listen('.chat.message.sent', async (event) => {
    await applyIncomingMessage(event);
  });
  userChannel.listen('.chat.presence.updated', (event) => {
    applyPresenceUpdate(event);
  });
  userChannel.listen('.chat.unread.updated', async () => {
    await loadConversations();
  });
};

const boot = async () => {
  try {
    await heartbeat();
    setupRealtime();
    await startConversationIfNeeded();
    await loadConversations();
    await loadMessages();

    refreshTimer = setInterval(async () => {
      await loadConversations();
      await loadMessages();
    }, 12000);

    heartbeatTimer = setInterval(async () => {
      await heartbeat();
    }, 30000);
  } catch (error) {
    errorText.value = error?.response?.data?.message || 'Unable to load chat right now.';
  }
};

onMounted(async () => {
  await boot();
});

onBeforeUnmount(() => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }

  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
  }

  if (window.Echo && Number(authUserId.value) > 0) {
    window.Echo.leave(`private-user.${authUserId.value}`);
  }

  userChannel = null;
});
</script>
